import { Alert, Box, Button, Chip, Stack } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useRef, useState } from 'react';

import AccountUserAutocomplete, { AccountUser } from '@/components/features/admin/components/AccountUserAutocomplete';
import OrganizationAutocomplete from '@/components/features/admin/components/OrganizationAutocomplete';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { useOrganizationsWithUsers } from '@/hooks/useOrganizationsWithUsers';
import { useTableNavigation } from '@/hooks/useTableNavigation';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import { ROUTES } from '@/routes';
import type { components } from '@/shared/api/openapi';
import { useTheme } from '@/theme';

type OrganizationResponse = components['schemas']['GetOrganizationsWithUsersEndpoint_OrganizationResponse'];

const columnHelper = createColumnHelper<OrganizationResponse>();

function OrganizationManagement() {
  const theme = useTheme();

  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<OrganizationResponse[]>([]);
  const [appliedOrganizationIds, setAppliedOrganizationIds] = useState<number[]>([]);
  const [selectedUserEmails, setSelectedUserEmails] = useState<AccountUser[]>([]);
  const [appliedUserEmails, setAppliedUserEmails] = useState<string[]>([]);

  const filterButtonRef = useRef<HTMLButtonElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { organizations, isLoading, error } = useOrganizationsWithUsers(
    apiPage,
    pagination.pageSize,
    null,
    appliedUserEmails,
    appliedOrganizationIds,
  );

  const { navigateToDetail } = useTableNavigation({
    containerRef: tableContainerRef,
  });

  const organizationsList = organizations?.items ?? [];

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Client',
        size: 30,
        cell: (info) => info.getValue() || '-',
      }),
      columnHelper.accessor('usersEmail', {
        header: 'Account Admin Emails',
        size: 70,
        cell: (info) => {
          const emails = info.getValue();
          const tablePagination = info.table.getState().pagination;
          const absoluteIndex = tablePagination.pageIndex * tablePagination.pageSize + info.row.index;

          const maxVisisbleEmails = 5;

          if (!emails || emails.length === 0) {
            return '-';
          }

          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {emails.slice(0, maxVisisbleEmails).map((email, index) => (
                <Chip
                  key={`email-${absoluteIndex}-${index}`}
                  label={email}
                  size='small'
                  sx={{
                    mr: 0.5,
                    mb: 0.5,
                    fontSize: theme.hhglobal.typography.body.base.fontSize,
                  }}
                />
              ))}
              {emails.length > maxVisisbleEmails && (
                <Chip
                  label={`+${emails.length - maxVisisbleEmails}`}
                  size='small'
                  sx={{
                    mr: 0.5,
                    mb: 0.5,
                    backgroundColor: 'transparent',
                    fontSize: theme.hhglobal.typography.body.large.fontSize,
                  }}
                />
              )}
            </Box>
          );
        },
      }),
    ],
    [theme],
  );

  const handleOrganizationChange = (_: React.SyntheticEvent, organizationObjs: OrganizationResponse[]) => {
    setSelectedOrganizationIds(organizationObjs);
    if (organizationObjs.length === 0) {
      setAppliedOrganizationIds([]);
    }
  };

  const handleUserEmailChange = (_: React.SyntheticEvent, users: AccountUser[]) => {
    setSelectedUserEmails(users);
    if (users.length === 0) {
      setAppliedUserEmails([]);
    }
  };

  const handleFilterApply = () => {
    setAppliedOrganizationIds(selectedOrganizationIds.map((org) => org.id!).filter((id): id is number => typeof id === 'number'));
    setAppliedUserEmails(selectedUserEmails.map((user) => user.email ?? '').filter(Boolean));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    filterButtonRef.current?.focus();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const handleRowClick = (organization: OrganizationResponse) => {
    if (organization.id) {
      const organizationDetailPath = ROUTES.ADMIN_ORGANIZATION_DETAILS.replace(':id', String(organization.id));
      navigateToDetail(organizationDetailPath, {
        organization,
      });
    }
  };

  const isFilterButtonDisabled =
    selectedOrganizationIds.length === 0 &&
    selectedUserEmails.length === 0 &&
    appliedOrganizationIds.length === 0 &&
    appliedUserEmails.length === 0;

  const tableWithData = useBaseTable<OrganizationResponse>({
    tableData: organizationsList,
    columns,
    isColumnOrderEnabled: false,
    isColumnVisibilityEnabled: false,
    isRowSelectionEnabled: false,
    hasPagination: true,
    paginationCount: organizations?.totalCount ?? 0,
    pagination,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
    onPaginationChange: setPagination,
  });

  if (error) {
    return (
      <Alert
        severity='error'
        sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}
        data-testid='error-loading-organizations'
      >
        Error loading organizations: {error.message}
      </Alert>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Stack
          direction='row'
          spacing={2}
          sx={{
            width: '100%',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Stack direction='row' spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <OrganizationAutocomplete value={selectedOrganizationIds} onChange={handleOrganizationChange} />
            <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-end' }}>
              <AccountUserAutocomplete value={selectedUserEmails} onChange={handleUserEmailChange} />
              <Button
                ref={filterButtonRef}
                variant='contained'
                sx={{
                  height: '36px',
                  fontSize: '14px',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: theme.hhglobal.typography.fontWeights.regular,
                  '&:active': {
                    backgroundColor: theme.hhglobal.neutral.gray900,
                    color: theme.palette.common.white,
                  },
                  '&:disabled': {
                    backgroundColor: theme.hhglobal.neutral.gray600,
                    color: theme.palette.common.white,
                  },
                }}
                data-testid='base-multi-autocomplete-filter'
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={handleFilterApply}
                disabled={isFilterButtonDisabled}
              >
                Filter
              </Button>
            </Stack>
          </Stack>
          <Button
            variant='contained'
            data-testid='add-organization-button'
            sx={{
              height: '36px',
              fontSize: '14px',
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: theme.hhglobal.typography.fontWeights.regular,
              '&:active': {
                backgroundColor: theme.hhglobal.neutral.gray900,
                color: theme.palette.common.white,
              },
              '&:disabled': {
                backgroundColor: theme.hhglobal.neutral.gray600,
                color: theme.palette.common.white,
              },
            }}
            onClick={() => navigateToDetail(ROUTES.ADMIN_ORGANIZATION_ADD)}
          >
            Add client
          </Button>
        </Stack>

        <BaseTable<OrganizationResponse>
          {...tableWithData}
          testId='organizations-table'
          ariaLabel='Organizations list'
          containerRef={tableContainerRef}
          noDataMessage='No results found for the selected filters.'
          onRowClick={(_, row) => handleRowClick(row.original)}
          loadingData={{
            isLoading: isLoading,
            loadingMessage: 'Loading Organizations…',
          }}
        />
      </Box>
    </>
  );
}

export default OrganizationManagement;
